import React, { useState, useEffect } from "react";
import TimeOffRequestsTableRow from "./TimeOffRequestsTableRow";
import TimeOffApprovedCalendarModal from "./TimeOffApprovedCalendarModal";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

export default function TimeOffRequestsTable(props) {
  const [open, setOpen] = useState(false);
  // Changed default sort direction to 'desc' for newest first
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [sortedRequests, setSortedRequests] = useState([]);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  // Initial sort and whenever props change
  useEffect(() => {
    if (props.timeOffRequests && props.timeOffRequests.length > 0) {
      const sorted = sortRequests(props.timeOffRequests, sortField, sortDirection);
      setSortedRequests(sorted);
    } else {
      setSortedRequests([]);
    }
  }, [props.timeOffRequests, sortField, sortDirection]);

  const hasTimeOffRequests = props.timeOffRequests.length > 0;

  // Handle sort header click
  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle direction if same field clicked
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New field, default to ascending
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Function to sort requests based on field and direction
  const sortRequests = (requests, field, direction) => {
    return [...requests].sort((a, b) => {
      let compareA, compareB;

      // Get the right values based on field
      switch (field) {
        case 'employee':
          compareA = a.store_team_member.first_name.toLowerCase();
          compareB = b.store_team_member.first_name.toLowerCase();
          break;
        case 'created_at':
          compareA = new Date(a.created_at);
          compareB = new Date(b.created_at);
          break;
        case 'date_lower_bound':
          compareA = new Date(a.date_lower_bound);
          compareB = new Date(b.date_lower_bound);
          break;
        default:
          compareA = a[field];
          compareB = b[field];
      }

      // Compare the values based on direction
      if (direction === 'asc') {
        if (compareA < compareB) return -1;
        if (compareA > compareB) return 1;
        return 0;
      } else {
        if (compareA > compareB) return -1;
        if (compareA < compareB) return 1;
        return 0;
      }
    });
  };

  // Table header with sort indicators
  const SortableHeader = ({ field, title, width, className = "" }) => {
    return (
      <th
        scope="col"
        className={`${width} px-3 py-3 text-left text-xs font-semibold text-slate-500 cursor-pointer hover:text-slate-700 group ${className}`}
        onClick={() => handleSort(field)}
      >
        <div className="flex items-center space-x-1">
          <span>{title}</span>
          <div className="flex flex-col">
            {sortField === field ? (
              sortDirection === 'asc' ? (
                <ArrowUpwardIcon sx={{ fontSize: 14 }} />
              ) : (
                <ArrowDownwardIcon sx={{ fontSize: 14 }} />
              )
            ) : (
              <span className="opacity-0 group-hover:opacity-30">
                <ArrowUpwardIcon sx={{ fontSize: 14 }} />
              </span>
            )}
          </div>
        </div>
      </th>
    );
  };


  return (
    <>
      <div className="mt-12">
        <div className="flex items-center px-6">
          <div className="flex items-center gap-x-4">
            <h1 className="text-xl font-bold text-slate-900">Requests</h1>
            <div
              data-help-id="time-off-calendar-button"
              className="px-5 py-2 bg-violet-50 hover:bg-violet-100 text-violet-700 text-xs rounded-lg h-fit cursor-pointer transition-colors"
              onClick={handleOpen}
            >
              <p>Time Off Calendar</p>
            </div>
          </div>
        </div>
        <div className="mt-8 flex flex-col">
          <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8 min-w-full">
            <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
              <div className="overflow-hidden">
                <table className="min-w-full divide-y divide-slate-100">
                  <thead>
                    <tr>
                      <SortableHeader
                        field="employee"
                        title="Employee"
                        width="w-[20%]"
                        className="pl-6 pr-3"
                      />
                      <SortableHeader
                        field="created_at"
                        title="Submitted"
                        width="w-[16%]"
                      />
                      <SortableHeader
                        field="date_lower_bound"
                        title="Period"
                        width="w-[20%]"
                      />
                      <th
                        scope="col"
                        className="w-[25%] px-3 py-3 text-left text-xs font-semibold text-slate-500"
                      >
                        Title
                      </th>
                      <th
                        scope="col"
                        className="w-[12%] px-3 py-3 text-left text-xs font-semibold text-slate-500"
                      >
                        Type
                      </th>
                      <th
                        scope="col"
                        className="w-1/12 relative py-3 pl-3 pr-6"
                      >
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white">
                    {sortedRequests.map((request, index) => (
                      <TimeOffRequestsTableRow
                        key={index}
                        firstName={request.store_team_member.first_name}
                        lastName={request.store_team_member.last_name}
                        title={request.title}
                        note={request.note}
                        time_off_type={request.time_off_type}
                        startDate={request.date_lower_bound}
                        endDate={request.date_upper_bound}
                        startTime={request.start_qhnum}
                        endTime={request.end_qhnum}
                        dateSubmitted={request.created_at}
                        storeTimezone={props.storeTimezone}
                        uuid={request.uuid}
                        respond_to_time_off_request={
                          props.respond_to_time_off_request
                        }
                      />
                    ))}
                  </tbody>
                </table>
                <div className="border-b border-slate-100"></div>
                {!hasTimeOffRequests &&
                  <div className="w-full mt-16 mb-8 flex items-center justify-center">
                    <div className="w-full flex flex-col items-center justify-center">
                      <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center">
                        <InfoOutlinedIcon sx={{ fontSize: 20, color: "#94a3b8" }} />
                      </div>
                      <p className="mt-3 text-sm text-slate-400">You have no time off requests</p>
                    </div>
                  </div>
                }
              </div>
            </div>
          </div>
        </div>
      </div>
      <TimeOffApprovedCalendarModal
        businessWeekStartDayIndex={props.businessWeekStartDayIndex}
        open={open}
        handleClose={handleClose}
        timeOffFutureApproved={props.timeOffFutureApproved}
        blackoutDates={props.blackoutDates}
        create_blackout_date={props.create_blackout_date}
        delete_blackout_date={props.delete_blackout_date}
        respond_to_time_off_request={props.respond_to_time_off_request}
        storeTimezone={props.storeTimezone}
      />
    </>
  );
}
